import React from 'react'
import styled from 'styled-components'
import { storyblokEditable, SbBlokData, Richtext } from '@storyblok/react'
import { containerDefault } from '../../lib/styles/Container'
import { SbAsset } from '../../types'
import { renderRichtext } from '../../lib/utils/renderRichtext'
import { ContactPanelV2 } from './ContactPanelV2'

const Container = styled.div<{ $padding?: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: flex-start;
  direction: ltr;

  > * {
    direction: ltr;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`

const Wrapper = styled.div<{ $blockBorderRadius?: string, $margin?: string }>`
    ${containerDefault}
    width: 100%;
    margin: ${props => props.$margin || '0rem auto'};
    border-radius: ${props => props.$blockBorderRadius || '0.5rem'};
    justify-content: center;
    background-color: transparent;

    @media (max-width: 768px) {
        padding: 0;
    }
`

const TextContent = styled.div`
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0;
  }
`

const Heading = styled.h2<{ $fontSize?: string; $textColor?: string }>`
  color: ${props => props.$textColor || 'var(--Black-black-1, #0D0D0D)'};
  font-size: ${props => props.$fontSize || '2rem'};
  font-style: normal;
  font-weight: 700;
  line-height: 115%;
  margin: 2rem 0 1rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
`

const Description = styled.p<{ $fontSize?: string; $textColor?: string }>`
  font-size: ${props => props.$fontSize || '0.9rem'};
  color: ${props => props.$textColor || 'inherit'};
  margin-bottom: 1.5rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`

const AssetList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`

const ImageWrapper = styled.img`
  width: 2rem;
  height: 2rem;
`

const AssetListHeading = styled.h3<{ $fontSize?: string; $textColor?: string }>`
  font-size: ${props => props.$fontSize || '0.875rem'};
  color: ${props => props.$textColor || 'inherit'};
  font-weight: 700;
  margin-bottom: 1rem;
`

export interface ContactModuleContent {
  blok: {
    heading: string
    description: string
    headingFontSize?: string
    headingColor?: string
    descriptionFontSize?: string
    descriptionColor?: string
    iconsTitle?: string
    iconsTitleColor?: string
    iconsTitleFontSize?: string
    assets?: SbAsset[]
    assetsTitle?: string
    assetsTitleColor?: string
    assetsTitleFontSize?: string
    padding?: string
    margin?: string
    richtext?: Richtext
    sendButtonText?: string
    confirmationText?: string
    emailLabel?: string
    firstNameLabel?: string
    lastNameLabel?: string
    productLabel?: string
    phoneNumberLabel?: string
    messageLabel?: string
  }
}


const ContactModuleContent = ({ blok }: ContactModuleContent & { blok: SbBlokData }) => {
  return (
    <Wrapper $margin={blok.margin}>
      <Container {...storyblokEditable(blok)} $padding={blok.padding}>
        <TextContent>
          <Heading
            $fontSize={blok.headingFontSize}
          >
            {blok.heading}
          </Heading>
          <Description
            $fontSize={blok.descriptionFontSize}
            $textColor={blok.descriptionColor}
          >
            {blok.description}
          </Description>

          {blok.assetsTitle && (
            <AssetListHeading
              $fontSize={blok.assetsTitleFontSize}
              $textColor={blok.assetsTitleColor}
            >
              {blok.assetsTitle}
            </AssetListHeading>
          )}

          {blok.assets && blok.assets.length > 0 && (
            <AssetList>
              {blok.assets.map((icon) => (
                <ImageWrapper src={icon.filename} alt={icon.alt} />
              ))}
            </AssetList>
          )}
          <div className="prose">{renderRichtext(blok.richtext as Richtext)}</div>
        </TextContent>
        <ContactPanelV2 blok={{
          headline: blok.iconsTitle,
          headlineElement: 'h2',
          headlineSize: 'is-h2',
          sendButtonText: blok.sendButtonText || 'Submit',
          marginBlock: '0',
          theme: 'azure',
          confirmationText: blok.confirmationText || 'Thank you for your message!',
          emailLabel: blok.emailLabel || 'Your work email',
          firstNameLabel: blok.firstNameLabel || 'Your first name',
          lastNameLabel: blok.lastNameLabel || 'Your last name',
          productLabel: blok.productLabel || 'Product you\'re interested in',
          phoneNumberLabel: blok.phoneNumberLabel || 'Your phone number (optional)',
          messageLabel: blok.messageLabel || 'Drop us a message (optional)',
          _uid: blok._uid,
          component: 'ContactPanel',
        }} />
      </Container>
    </Wrapper>
  )
}

export default ContactModuleContent